import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useMarketingCopy } from '../context/MarketingCopyContext';
import { localizedPath } from '../utils/routes';
import SEOHead from '../components/SEOHead';

export default function About() {
  const { t, language } = useLanguage();
  const { lang } = useParams();
  const currentLang = lang || language;
  const lp = (routeName) => localizedPath(routeName, currentLang);
  const { mc } = useMarketingCopy();
  const [showStoryImg, setShowStoryImg] = useState(true);
  const [showTeamImg, setShowTeamImg] = useState(true);

  return (
    <div style={{ background: '#0C1220', minHeight: '100vh', paddingTop: 'var(--cjpa-nav-offset)' }}>
      <SEOHead page="about" />
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: 'clamp(2rem, 4vw, 4rem) clamp(1rem, 3vw, 2rem)' }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
          <div style={{ fontSize: '0.6rem', letterSpacing: '0.1em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '1rem' }}>
            {mc('about', 'subtitle', t.about.subtitle)}
          </div>
          <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 'clamp(3rem, 6vw, 5rem)', color: '#E8ECF0', fontWeight: 300 }}>
            {t.about.title} <span style={{ color: '#D42B2B', fontStyle: 'italic' }}>{t.about.titleHighlight}</span>
          </h1>
        </div>

        {/* Mission */}
        <section style={{ marginBottom: '5rem' }}>
          <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2.5rem', color: '#D42B2B', fontWeight: 300, marginBottom: '1.5rem', textAlign: 'center' }}>
            {mc('about', 'mission.title', t.about.mission.title)}
          </h2>
          <p style={{ color: '#64748B', fontSize: '1.05rem', lineHeight: 1.9, maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            {mc('about', 'mission.text', t.about.mission.text)}
          </p>
        </section>

        {/* Our Story / Notre Histoire */}
        <section style={{ marginBottom: '5rem' }}>
          <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.1)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: 'linear-gradient(180deg, #D42B2B 0%, rgba(212,43,43,0.1) 100%)', zIndex: 1 }} />
            {showStoryImg && (
              <div style={{ width: '100%', maxHeight: '280px', overflow: 'hidden' }}>
                <img
                  src="/images/about/workshop-interior.jpg"
                  alt={t.images?.aboutShop || ''}
                  width={800}
                  height={280}
                  loading="lazy"
                  style={{ width: '100%', height: '280px', objectFit: 'cover', filter: 'brightness(0.65)', display: 'block' }}
                  onError={() => setShowStoryImg(false)}
                />
              </div>
            )}
            <div style={{ padding: 'clamp(2.5rem, 5vw, 4.5rem) clamp(1.5rem, 4vw, 4rem)' }}>
            <div style={{ maxWidth: '750px', margin: '0 auto' }}>
              <div style={{ fontSize: '0.6rem', letterSpacing: '0.15em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '0.8rem', textAlign: 'center' }}>
                Est. 2016
              </div>
              <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2.5rem', color: '#D42B2B', fontWeight: 300, marginBottom: '2.5rem', textAlign: 'center' }}>
                {mc('about', 'history.title', t.about.history.title)}
              </h2>
              {t.about.history.paragraphs.map((p, i) => (
                <p key={i} style={{
                  color: i === 3 ? '#E8ECF0' : '#94A3B8',
                  fontSize: i === 3 ? '1.1rem' : '0.95rem',
                  lineHeight: 1.9,
                  marginBottom: i < t.about.history.paragraphs.length - 1 ? '1.5rem' : 0,
                  fontStyle: i === 3 ? 'italic' : 'normal',
                  fontFamily: i === 3 ? "'Rajdhani', sans-serif" : "'Barlow', sans-serif",
                  fontWeight: i === 3 ? 500 : 400,
                  textAlign: i === 3 ? 'center' : 'left',
                }}>
                  {mc('about', `history.p${i}`, p)}
                </p>
              ))}
            </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section style={{ background: 'rgba(148,163,184,0.03)', border: '1px solid rgba(148,163,184,0.1)', padding: 'clamp(2rem, 4vw, 4rem) clamp(1.5rem, 3vw, 3rem)', marginBottom: 'clamp(2rem, 4vw, 5rem)' }}>
          <div className="cjpa-why-grid">
            <div>
              <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2.5rem', color: '#D42B2B', fontWeight: 300, marginBottom: '1.5rem' }}>
                {mc('about', 'team.title', t.about.team.title)}
              </h2>
              <p style={{ color: '#64748B', fontSize: '1rem', lineHeight: 1.9 }}>
                {mc('about', 'team.text', t.about.team.text)}
              </p>
            </div>
            {showTeamImg && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src="/images/about/team-at-work.jpg"
                  alt={t.images?.aboutTeam || ''}
                  width={800}
                  height={600}
                  loading="lazy"
                  style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderLeft: '4px solid #D42B2B', display: 'block' }}
                  onError={() => setShowTeamImg(false)}
                />
              </div>
            )}
          </div>
        </section>

        {/* Values Grid */}
        <section style={{ marginBottom: '5rem' }}>
          <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2.5rem', color: '#D42B2B', fontWeight: 300, marginBottom: '3rem', textAlign: 'center' }}>
            {t.about.values.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1px', background: 'rgba(148,163,184,0.1)' }}>
            {[
              [mc('about', 'values.quality', t.about.values.quality), mc('about', 'values.qualityText', t.about.values.qualityText)],
              [mc('about', 'values.transparency', t.about.values.transparency), mc('about', 'values.transparencyText', t.about.values.transparencyText)],
              [mc('about', 'values.expertise', t.about.values.expertise), mc('about', 'values.expertiseText', t.about.values.expertiseText)],
              [mc('about', 'values.community', t.about.values.community), mc('about', 'values.communityText', t.about.values.communityText)]
            ].map(([title, text]) => (
              <div key={title} style={{ background: '#111827', padding: '2.5rem' }}>
                <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.5rem', color: '#D42B2B', marginBottom: '1rem', fontWeight: 400 }}>
                  {title}
                </h3>
                <p style={{ color: '#94A3B8', fontSize: '0.9rem', lineHeight: 1.8 }}>
                  {text}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Business Info */}
        <section style={{ background: 'rgba(148,163,184,0.05)', border: '1px solid rgba(148,163,184,0.15)', padding: 'clamp(2rem, 4vw, 4rem) clamp(1.5rem, 3vw, 3rem)', textAlign: 'center' }}>
          <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2.2rem', color: '#E8ECF0', fontWeight: 300, marginBottom: '1rem' }}>
            {mc('about', 'visit', t.about.visit)}
          </h2>
          <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.8, marginBottom: '2.5rem', maxWidth: '600px', margin: '0 auto 2.5rem' }}>
            {mc('about', 'visitText', t.about.visitText)}
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', marginTop: '3rem' }}>
            <div>
              <div style={{ fontSize: '0.65rem', letterSpacing: '0.1em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                {t.contact.visitUs}
              </div>
              <div style={{ color: '#D42B2B', fontSize: '0.95rem', lineHeight: 1.6 }}>
                {t.business.address}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.65rem', letterSpacing: '0.1em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                {t.contact.callUs}
              </div>
              <a href={`tel:${mc('contact', 'phone1', t.business.phone)}`} style={{ color: '#D42B2B', fontSize: '1.1rem', textDecoration: 'none', fontFamily: "'Rajdhani', sans-serif", display: 'block' }}>
                {mc('contact', 'phone1', t.business.phone)}
              </a>
              <a href={`tel:${mc('contact', 'phone2', t.business.phone2)}`} style={{ color: '#D42B2B', fontSize: '0.95rem', textDecoration: 'none', fontFamily: "'Rajdhani', sans-serif", display: 'block', marginTop: '0.3rem' }}>
                {mc('contact', 'phone2', t.business.phone2)}
              </a>
            </div>
            <div>
              <div style={{ fontSize: '0.65rem', letterSpacing: '0.1em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                {t.business.hours.title}
              </div>
              <div style={{ color: '#64748B', fontSize: '0.9rem', lineHeight: 1.7 }}>
                {t.business.hours.monday}–{t.business.hours.friday}<br />
                {t.business.hours.weekdaysTime}
              </div>
            </div>
          </div>

          <Link to={lp('book')} style={{
            display: 'inline-block', marginTop: '3rem',
            background: '#D42B2B', color: '#0C1220', border: 'none',
            padding: '1.1rem 3rem', textDecoration: 'none',
            fontFamily: "'Barlow', sans-serif", fontSize: '0.7rem',
            letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600,
            boxShadow: '0 0 40px rgba(148,163,184,0.25)',
          }}>
            {t.home.cta.button}
          </Link>
        </section>

      </div>
    </div>
  );
}
